/* TTS homepage size picker (sections/tts-size-picker.liquid), custom element <tts-size-picker>.
   - Loads the in-stock model count per EU size once (Section Rendering API on the collection URL with
     filter.v.availability=1; the counts come from snippets/tts-size-memory.liquid) and caches it for
     10 minutes in sessionStorage.
   - Hides sizes with 0 models, fills "<n> modell", rewrites every tile link with the exact filter values
     of the current storefront language (on /ro: "UE42", "UE 42" and "EU42").
   - Highlights the size remembered by window.TTSSizeMemory (assets/tts-size-memory.js) and saves the
     tapped size before the browser follows the link. */
(function () {
  'use strict';

  if (!window.customElements || customElements.get('tts-size-picker')) return;

  var RE = /^\s*(?:EU|UE)\s*(\d{2})(?:[.,](\d)|\s+(\d)\/(\d))?\s*$/i;
  var TTL = 10 * 60 * 1000;
  var CACHE_PREFIX = 'tts_sp_v1:';
  /* Same session flag as the collection size row: its "Clear" then also removes the in-stock filter. */
  var KEY_AVAIL = 'tts_size_avail_added';

  function parse(value) {
    var m = RE.exec(String(value == null ? '' : value));
    if (!m) return null;
    return { base: parseInt(m[1], 10), whole: !m[2] && !m[3] };
  }

  function session() {
    try {
      return window.sessionStorage;
    } catch (e) {
      return null;
    }
  }

  function readCache(key) {
    try {
      var s = session();
      var raw = s && s.getItem(key);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (!data || !data.t || Date.now() - data.t > TTL || !Array.isArray(data.values)) return null;
      return data;
    } catch (e) {
      return null;
    }
  }

  function writeCache(key, data) {
    try {
      var s = session();
      if (s) s.setItem(key, JSON.stringify(data));
    } catch (e) {
      /* storage full or blocked: the counts are simply loaded again next time */
    }
  }

  function memory() {
    try {
      return window.TTSSizeMemory ? window.TTSSizeMemory.get() : null;
    } catch (e) {
      return null;
    }
  }

  function fetchValues(url, sectionId) {
    var sep = url.indexOf('?') === -1 ? '?' : '&';
    var target = url + sep + 'filter.v.availability=1&section_id=' + encodeURIComponent(sectionId);
    return fetch(target, { credentials: 'same-origin' })
      .then(function (r) {
        return r.ok ? r.text() : '';
      })
      .then(function (html) {
        if (!html) return null;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var row = doc.querySelector('tts-size-memory');
        var json = doc.querySelector('script[data-tts-size-values]');
        if (!row || !json) return null;
        var values = JSON.parse(json.textContent || '[]');
        if (!Array.isArray(values)) return null;
        return {
          t: Date.now(),
          param: row.getAttribute('data-param') || '',
          availParam: row.getAttribute('data-avail-param') || 'filter.v.availability',
          availValue: row.getAttribute('data-avail-value') || '1',
          values: values
        };
      })
      .catch(function () {
        return null;
      });
  }

  class TTSSizePicker extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.url = this.dataset.url || '';
      this.list = this.querySelector('[data-list]');
      this.items = Array.prototype.slice.call(this.querySelectorAll('[data-item]'));
      this.data = null;

      try {
        this.plural = new Intl.PluralRules(this.dataset.locale || 'hu');
      } catch (e) {
        this.plural = null;
      }

      this.onClick = this.onClick.bind(this);
      this.onMemory = this.onMemory.bind(this);
      this.addEventListener('click', this.onClick);
      document.addEventListener('tts:size-change', this.onMemory);

      this.markSaved();
      this.load();
    }

    disconnectedCallback() {
      document.removeEventListener('tts:size-change', this.onMemory);
    }

    load() {
      var self = this;
      if (!this.url || !window.fetch || !window.DOMParser) {
        this.finish(null);
        return;
      }
      var key = CACHE_PREFIX + this.url;
      var cached = readCache(key);
      if (cached) {
        this.finish(cached);
        return;
      }
      /* This section rendered in the collection's context is tiny; the main collection section is the fallback. */
      fetchValues(this.url, 'tts-size-picker')
        .then(function (data) {
          return data || fetchValues(self.url, 'main-collection');
        })
        .then(function (data) {
          if (data && data.param) writeCache(key, data);
          self.finish(data && data.param ? data : null);
        });
    }

    /* Group the filter values by whole EU size: count = in-stock models in that exact whole size. */
    group(data) {
      var byBase = {};
      data.values.forEach(function (x) {
        var p = parse(x && x.v);
        if (!p) return;
        var b = byBase[p.base] || (byBase[p.base] = { whole: [], frac: [], count: 0 });
        if (p.whole) {
          b.whole.push(x.v);
          b.count += parseInt(x.c, 10) || 0;
        } else {
          b.frac.push(x.v);
        }
      });
      return byBase;
    }

    label(count) {
      var form = 'other';
      if (this.plural) {
        try {
          form = this.plural.select(count);
        } catch (e) {
          form = 'other';
        }
      }
      var tpl = (form === 'one' && this.dataset.one) || (form === 'few' && this.dataset.few) || this.dataset.other || '[n]';
      return tpl.replace('[n]', String(count));
    }

    /* The link matches the count on the tile: the whole size only + in stock, also with a remembered
       half size (e.g. 42.5). Half sizes stay an option of the collection size row. */
    href(b) {
      var d = this.data;
      var params = new URLSearchParams();
      b.whole.forEach(function (v) {
        params.append(d.param, v);
      });
      params.append(d.availParam, d.availValue);
      return this.url + '?' + params.toString();
    }

    finish(data) {
      var self = this;
      this.data = data;
      this.byBase = data ? this.group(data) : null;
      var visible = 0;

      this.items.forEach(function (item) {
        var base = parseInt(item.getAttribute('data-item'), 10);
        var countEl = item.querySelector('[data-count]');
        var link = item.querySelector('a[data-base]');
        if (!self.byBase) {
          /* No data: keep every tile with its server-rendered link, without a count. */
          if (countEl) countEl.textContent = '';
          visible++;
          return;
        }
        var b = self.byBase[base];
        if (!b || b.count < 1) {
          item.hidden = true;
          return;
        }
        item.hidden = false;
        visible++;
        if (countEl) countEl.textContent = self.label(b.count);
        if (link) link.setAttribute('href', self.href(b));
      });

      if (this.list) this.list.setAttribute('aria-busy', 'false');
      this.classList.add('is-loaded');

      /* The collection has no in-stock sizes at all: hide the whole section (never in the theme editor). */
      if (this.byBase && visible === 0 && !(window.Shopify && window.Shopify.designMode)) {
        var sectionEl = this.closest('.shopify-section');
        if (sectionEl) sectionEl.hidden = true;
      }

      this.markSaved();
    }

    markSaved() {
      var mem = memory();
      var saved = null;
      this.items.forEach(function (item) {
        var link = item.querySelector('a[data-base]');
        if (!link) return;
        var on = !!mem && String(mem.base) === link.getAttribute('data-base') && !item.hidden;
        link.classList.toggle('is-saved', on);
        var hint = link.querySelector('[data-saved-label]');
        if (hint) hint.hidden = !on;
        if (on) saved = item;
      });
      /* Keep the saved size in view inside the mobile strip without scrolling the page. */
      if (saved && this.list && this.list.scrollWidth > this.list.clientWidth) {
        this.list.scrollLeft = Math.max(0, saved.offsetLeft - (this.list.clientWidth - saved.offsetWidth) / 2);
      }
    }

    onMemory() {
      var self = this;
      if (this.byBase) {
        this.items.forEach(function (item) {
          var base = parseInt(item.getAttribute('data-item'), 10);
          var b = self.byBase[base];
          var link = item.querySelector('a[data-base]');
          if (b && b.count > 0 && link) link.setAttribute('href', self.href(b));
        });
      }
      this.markSaved();
    }

    onClick(event) {
      var link = event.target.closest && event.target.closest('a[data-base]');
      if (!link || !this.contains(link)) return;
      var base = parseInt(link.getAttribute('data-base'), 10);
      if (window.TTSSizeMemory) {
        var mem = memory();
        /* Tapping the saved number again keeps a remembered half size (42.5 stays 42.5). */
        window.TTSSizeMemory.set(mem && mem.base === base ? mem.size : String(base));
      }
      try {
        var s = session();
        if (s) s.setItem(KEY_AVAIL, '1');
      } catch (e) {
        /* ignore */
      }
    }
  }

  customElements.define('tts-size-picker', TTSSizePicker);
})();
